import { Container, Section } from "@/components/ui/section";
import { Card, CardContent } from "@/components/ui/card";
import { Megaphone, Handshake, Users, Lightbulb, Check, Search, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const useCases = [
  {
    icon: Megaphone,
    title: "For Marketing & PMM",
    points: [
      "Generate stories, case studies, and landing page content.",
      "Maintain a constant flow of fresh proof points.",
      "Support product launches with real customer stories."
    ]
  },
  {
    icon: Handshake,
    title: "For Sales & Enablement",
    points: [
      "Industry or persona-specific proof to close deals.",
      "One-pagers ready to send in seconds.",
      'Eliminate "Do we have a customer like X?" forever.'
    ]
  },
  {
    icon: Users,
    title: "For Customer Success",
    points: [
      "Scalable customer check-ins beyond NPS.",
      "Surface expansion and risk signals.",
      "Capture success stories early."
    ]
  },
  {
    icon: Lightbulb,
    title: "For Product & Founders",
    points: [
      "Understand usage patterns across segments.",
      "Validate roadmap decisions with real interviews.",
      'Maintain a "voice of customer" library.'
    ]
  }
];

export const UseCases = () => {
  return (
    <Section id="use-cases" className="bg-background">
      <Container>
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            Built for teams who need more customer proof, faster.
          </h2>
          <p className="text-lg text-muted-foreground">
            Ideal for B2B companies with 200–10,000 customers.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {useCases.map((useCase, index) => (
            <Card key={index} className="border-border hover:shadow-[var(--shadow-soft)] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <useCase.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground pt-2">
                    {useCase.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {useCase.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}

          {/* Market Research Use Case */}
          <Link to="/market-research-insights" className="block group">
            <Card className="border-border hover:shadow-[var(--shadow-soft)] hover:border-primary/50 transition-all duration-300 h-full">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Search className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground pt-2">
                    For Market Research
                  </h3>
                </div>
                <ul className="space-y-3 flex-grow">
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">Conduct AI-led voice interviews at scale.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">Uncover qualitative insights faster than ever.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">Multi-channel: chat, SMS, web & phone calls.</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-primary font-medium">
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </Container>
    </Section>
  );
};
